import React, { useState, useEffect, useRef, useCallback } from 'react';
import { track, getSlug } from '@/utils/tracker';
import { useNavigate } from 'react-router-dom';
import { analysisApi } from '@/services/analysisApi';
import DemoExplainer, { InfoTip } from '@/components/DemoExplainer';
import InlineMessage from '@/components/InlineMessage';
import HintBar from '@/components/HintBar';

const HINTS = [
  'Each column shows tasks grouped by recommended 4B deployment',
  'Toggle sub-tasks to see the full activity breakdown',
  'Configure each role before generating the Blueprint',
  'AI-Addressable tasks have the highest automation opportunity',
];

/* ================================================================
   DESIGN TOKENS
   ================================================================ */
// Design tokens sourced from tokens.css — use var(--token) in JSX styles
// Colour aliases for JS references (mirrors tokens.css)
const C = {
  bg:       '#0A0A1A',
  bg2:      '#0D0D20',
  bg3:      'rgba(255,255,255,0.03)',
  bg4:      'rgba(255,255,255,0.06)',
  text:     '#FFFFFF',
  t2:       'rgba(255,255,255,0.92)',
  t3:       'rgba(255,255,255,0.72)',
  t4:       'rgba(255,255,255,0.48)',
  line:     'rgba(255,255,255,0.10)',
  lines:    'rgba(255,255,255,0.07)',
  bot:      '#00D4C8',
  borrow:   '#06B6D4',
  build:    '#C9A84C',
  bi:       '#E8B96A',
  buy:      '#EC4899',
  teal:     '#00D4C8',
  gold:     '#C9A84C',
  violet:   '#E8B96A',
  pink:     '#EC4899',
  cyan:     '#06B6D4',
  amber:    '#C9A84C',
  sigAmber: '#F59E0B',
  sigRed:   '#FF4444',
  sigGreen: '#10B981',
  muted:    'rgba(255,255,255,0.48)',
  surface:  '#1A1A2E',
  surface2: '#22223E',
  // ── Retained pre-existing keys (used elsewhere in this file) ──
  card:        '#111B2E',
  border:      'rgba(255,255,255,0.07)',
  borderLight: 'rgba(255,255,255,0.12)',
  white:       '#F2F6FA',
  fog:         '#6B7E91',
  green:       '#2EC8A0',
  red:         '#FF3B5C',
  lightBg:     '#FAFAFA',
  lightText:   '#0D1117',
  lightMuted:  '#6B7280',
  lightBorder: '#E5E7EB',
};
const STEPS = ['Upload', 'Discipline', 'Analysis', 'Blueprint'];
const DISC_LABELS = {
  operational_excellence: 'Operational Excellence',
  customer_intimacy: 'Customer Intimacy',
  product_leadership: 'Product Leadership',
  growth_capacity: 'Growth Capacity',
};
const REDEPLOY_TARGETS = {
  Finance: ['FP&A Analyst', 'Finance Business Partner'],
  Technology: ['Engineering Lead', 'Product Manager'],
  HR: ['HR Business Partner', 'L&D Specialist'],
  Operations: ['Operations Manager', 'Process Analyst'],
  Sales: ['Sales Enablement Lead', 'Revenue Ops Analyst'],
  Marketing: ['Brand Strategist', 'Growth Lead'],
};
const AVG_HOURLY = 45;

/* ================================================================
   ANIMATED NUMBER
   ================================================================ */
const AnimNum = ({ value, prefix = '', suffix = '', decimals = 0, dur = 600 }) => {
  const [disp, setDisp] = useState(0);
  const prev = useRef(0);
  const raf = useRef(null);
  useEffect(() => {
    const from = prev.current;
    const to = value;
    prev.current = to;
    let start = null;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 4);
      setDisp(from + (to - from) * ease);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [value, dur]);
  const fmt = decimals > 0 ? disp.toFixed(decimals) : Math.round(disp).toLocaleString();
  return <>{prefix}{fmt}{suffix}</>;
};

/* ================================================================
   STEP INDICATOR (light)
   ================================================================ */
const StepBar = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
    <span style={{ fontSize: 13, color: C.lightMuted, fontFamily: 'Inter,sans-serif' }}>Step 3 of 4</span>
    <div style={{ display: 'flex', gap: 4 }}>
      {STEPS.map((s, i) => (
        <div key={s} style={{ width: 32, height: 4, borderRadius: 2, background: i <= 2 ? C.teal : C.lightBorder, transition: 'background 0.3s' }} />
      ))}
    </div>
  </div>
);

/* ================================================================
   TASK CARD
   ================================================================ */
const SUB_LABEL_DISPLAY = {
  ai_addressable: 'AI-Addressable',
  fractional: 'Fractional',
  interim: 'Interim',
  advisory: 'Advisory',
  human_strategic: 'Human Strategic',
  inclusion_pathway: 'Inclusion Pathway',
  buy: 'New Hire',
};
const SUB_LABEL_COLOR = {
  ai_addressable: '#7C5CFC',
  fractional: '#C9A84C',
  interim: '#FF3B5C',
  advisory: '#4A9EFF',
  human_strategic: '#00D4C8',
  inclusion_pathway: '#2EC8A0',
  buy: '#F2F6FA',
};

const TaskCard = ({ task, hours, selected, color, onToggle, subLabel, roleTitle, roleFunction, category4b, onSubtasksLoaded, subtaskData }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subtasks, setSubtasks] = useState(subtaskData || null);

  const handleExpand = async (e) => {
    e.stopPropagation();
    if (subtasks) { setExpanded(!expanded); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const resp = await fetch(`${API}/api/analysis/subtasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          task, role_title: roleTitle || 'Unknown Role',
          function: roleFunction || 'General',
          estimated_hours: hours || 40,
          category_4b: category4b || 'bot'
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setSubtasks(data);
        setExpanded(true);
        if (onSubtasksLoaded) onSubtasksLoaded(task, data);
      }
    } catch(err) { console.error('Subtask fetch failed:', err); }
    finally { setLoading(false); }
  };

  const cat4bColor = { bot: C.violet, borrow: C.gold, build: C.teal, buy: C.green };

  // Map sub_label → recipe pill class
  const pillClass = (
    subLabel === 'ai_addressable' ? 'pill pill-ai'
    : subLabel === 'inclusion_pathway' ? 'pill pill-incl'
    : 'pill pill-human'
  );

  return (
    <div style={{ marginBottom: 8 }}>
      <div className="task-card" style={{
        border: selected ? `1px solid ${color}` : undefined,
        background: selected ? `${color}12` : undefined,
        borderRadius: expanded ? '10px 10px 0 0' : undefined,
        marginBottom: 0,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <button onClick={onToggle} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <div className="task-name">{task}</div>
            <div className="task-meta" style={{ marginTop: 6, justifyContent: 'flex-start' }}>
              {subLabel && (
                <span className={pillClass}>
                  {SUB_LABEL_DISPLAY[subLabel] || subLabel}
                </span>
              )}
              <span className="task-hrs">{hours} hrs/yr</span>
              {subtasks && (
                <span className="task-hrs" style={{ color: 'var(--teal)' }}>
                  {subtasks.realistic_automation_pct}% realistic vs {subtasks.theoretical_automation_pct}% theoretical
                </span>
              )}
            </div>
          </button>
          <button onClick={handleExpand} style={{
            flexShrink: 0, padding: '3px 8px', borderRadius: 6, fontSize: 10,
            background: 'none', border: `1px solid ${C.border}`, color: C.muted,
            cursor: loading ? 'wait' : 'pointer', whiteSpace: 'nowrap',
          }}>
            {loading ? '...' : expanded ? '▴ collapse' : '▾ sub-tasks'}
          </button>
        </div>
      </div>
      {expanded && subtasks && (
        <div style={{
          background: '#0A1628', border: `1px solid ${C.border}`, borderTop: 'none',
          borderRadius: '0 0 10px 10px', padding: '10px 14px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sub-task breakdown</span>
            <div style={{ display: 'flex', gap: 12 }}>
              <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.amber }}>
                Theoretical: {subtasks.theoretical_automation_pct}%
              </span>
              <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.teal }}>
                Realistic: {subtasks.realistic_automation_pct}%
              </span>
            </div>
          </div>
          {subtasks.subtasks.map((st, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
              borderBottom: i < subtasks.subtasks.length - 1 ? `1px solid ${C.border}` : 'none',
            }}>
              <span style={{
                fontFamily: "'Overpass Mono', monospace", fontSize: 8, padding: '2px 6px',
                borderRadius: 4, background: `${cat4bColor[st.category_4b] || C.fog}20`,
                color: cat4bColor[st.category_4b] || C.fog, textTransform: 'uppercase',
                flexShrink: 0, letterSpacing: '0.05em',
              }}>{st.category_4b}</span>
              <p style={{ flex: 1, fontSize: 12, color: C.white, lineHeight: 1.4 }}>{st.description}</p>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.muted, display: 'block' }}>{st.hours}h</span>
                {st.category_4b === 'bot' && (
                  <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.violet }}>{st.automation_pct}% auto</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ================================================================
   METRIC CARD (Phase 2)
   ================================================================ */
const MetricCard = ({ label, children, sub }) => (
  <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', flex: '1 1 140px', minWidth: 140 }}>
    <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</p>
    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: C.white, lineHeight: 1 }}>{children}</div>
    {sub && <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.fog, marginTop: 4 }}>{sub}</p>}
  </div>
);

/* ================================================================
   NUDGE CARD
   ================================================================ */
const NudgeCard = ({ text, actions, onDismiss }) => (
  <div style={{
    background: `${C.amber}10`, border: `1px solid ${C.amber}30`, borderRadius: 10,
    padding: '12px 16px', marginBottom: 12,
    animation: 'slideIn 250ms ease-out',
  }}>
    <p style={{ fontSize: 13, color: C.amber, lineHeight: 1.5, marginBottom: 8 }}>{text}</p>
    <div style={{ display: 'flex', gap: 8 }}>
      {actions.map((a, i) => (
        <button key={i} onClick={a.onClick || onDismiss} style={{
          padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
          background: i === 0 ? C.amber : 'transparent',
          color: i === 0 ? C.bg : C.muted,
          border: i === 0 ? 'none' : `1px solid ${C.border}`,
        }}>{a.label}</button>
      ))}
    </div>
  </div>
);

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
const AnalysisResults = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [func, setFunc] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [activeRole, setActiveRole] = useState(0);
  const [generating, setGenerating] = useState(false);

  // Per-role state: { [roleIdx]: { phase, selected, allocations, newRoles } }
  const [roleStates, setRoleStates] = useState({});
  const [nudge, setNudge] = useState(null);
  const [selectedFreedTask, setSelectedFreedTask] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [inlineMsg, setInlineMsg] = useState(null);

  const [restoredFromSession, setRestoredFromSession] = useState(false);

  const _trackSlug = getSlug();
  const _trackEntry = useRef(Date.now());

  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  useEffect(() => {
    track(_trackSlug, 'screen_viewed', 'analysis');
    return () => {
      const dur = Math.round((Date.now() - _trackEntry.current) / 1000);
      track(_trackSlug, 'screen_exited', 'analysis', dur);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load data
  useEffect(() => {
    const sid = localStorage.getItem('reai_session_id') || localStorage.getItem('analysis_session_id');
    if (!sid) { navigate('/analysis'); return; }

    const f = localStorage.getItem('analysis_function') || '';
    const d = localStorage.getItem('analysis_discipline') || '';
    const raw = localStorage.getItem('analysis_mol_results');

    // If we have cached results in localStorage, use them
    if (raw) {
      const parsed = JSON.parse(raw);
      setFunc(f);
      setDiscipline(d);
      setData(parsed);
      _initRoleStates(parsed, sid);
      return;
    }

    // Otherwise try to restore from the session API
    analysisApi.getSession(sid).then((res) => {
      const session = res.data?.session;
      if (!session) { navigate('/analysis'); return; }

      setFunc(session.function || f);
      setDiscipline(session.value_discipline || d);

      if (session.molecularisation_results) {
        // Session has results already - restore without re-running
        const parsed = {
          session_id: session.id,
          value_discipline: session.value_discipline,
          roles: session.molecularisation_results,
        };
        setData(parsed);
        setRestoredFromSession(true);
        // Sync to localStorage for future page loads
        localStorage.setItem('analysis_function', session.function || '');
        localStorage.setItem('analysis_discipline', session.value_discipline || '');
        localStorage.setItem('analysis_mol_results', JSON.stringify(parsed));
        _initRoleStates(parsed, sid);
      } else {
        // No results yet - redirect to discipline step
        navigate('/analysis/discipline');
      }
    }).catch(() => { navigate('/analysis'); });
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const _initRoleStates = (parsed, sid) => {
    // Try restore persisted phase2 state
    const saved = localStorage.getItem(`reai_phase2_state_${sid}`);
    if (saved) {
      try { setRoleStates(JSON.parse(saved)); return; } catch { /* ignore */ }
    }
    // Init fresh state
    const init = {};
    (parsed.roles || []).forEach((_, i) => {
      init[i] = { phase: 1, selected: { bot: [], build: [], borrow: [], buy: [] }, allocations: {}, newRoles: [] };
    });
    setRoleStates(init);
  };

  // Persist state
  const persist = useCallback((st) => {
    const sid = localStorage.getItem('reai_session_id') || localStorage.getItem('analysis_session_id');
    if (sid) localStorage.setItem(`reai_phase2_state_${sid}`, JSON.stringify(st));
  }, []);

  const updateRole = useCallback((idx, patch) => {
    setRoleStates((prev) => {
      const next = { ...prev, [idx]: { ...prev[idx], ...patch } };
      persist(next);
      return next;
    });
  }, [persist]);

  // Inject animation keyframes
  useEffect(() => {
    if (document.getElementById('ar-kf')) return;
    const s = document.createElement('style');
    s.id = 'ar-kf';
    s.textContent = `@keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}`;
    document.head.appendChild(s);
  }, []);

  if (!data) return null;

  const roles = data.roles || [];
  const role = roles[activeRole];
  const defaultSelected = { bot: [], build: [], borrow: [], buy: [] };
  const rawRs = roleStates[activeRole] || { phase: 1, selected: defaultSelected, allocations: {}, newRoles: [] };
  // Normalize selected state: migrate from old format or fix missing keys
  const rs = {
    ...rawRs,
    selected: {
      bot: rawRs.selected?.bot || rawRs.selected?.auto || [],
      build: rawRs.selected?.build || rawRs.selected?.di || [],
      borrow: rawRs.selected?.borrow || rawRs.selected?.disc || [],
      buy: rawRs.selected?.buy || [],
    },
  };
  if (!role) return null;

  const isFilled = role.filled !== false;
  const tasks = role.task_breakdown || [];
  const totalHrs = tasks.reduce((s, t) => s + Math.round(((t.bot_score || t.automation_score || 0.3) + (t.build_score || t.di_score || 0.3)) / 2 * 2.5 * 80), 0) || tasks.length * 150;
  const taskHrs = (t) => Math.max(40, Math.round(((t.bot_score || t.automation_score || 0.3) + (t.build_score || t.di_score || 0.3)) / 2 * 200));

  // 4B categorisation — task lands in exactly one column. Use category_4b
  // (set by _categorise_task_4b argmax in the backend, or by Claude in the
  // JD-upload path) as primary, sub_label as fallback for legacy rows, and
  // 'build' as the last-resort default. No score-threshold fallbacks —
  // those caused the same task to render in 2-3 columns simultaneously
  // whenever multiple scores crossed their individual thresholds.
  const allWithIdx = tasks.map((t, i) => ({ ...t, idx: i, hrs: t.estimated_hours || taskHrs(t) }));
  const bucketOf = (t) => {
    if (t.category_4b) return t.category_4b;
    if (t.sub_label === 'ai_addressable') return 'bot';
    if (t.sub_label === 'buy') return 'buy';
    if (t.sub_label === 'fractional' || t.sub_label === 'interim' || t.sub_label === 'advisory') return 'borrow';
    if (t.sub_label === 'human_strategic' || t.sub_label === 'inclusion_pathway') return 'build';
    return 'build';
  };
  const botTasks    = allWithIdx.filter(t => bucketOf(t) === 'bot');
  const buildTasks  = allWithIdx.filter(t => bucketOf(t) === 'build');
  const borrowTasks = allWithIdx.filter(t => bucketOf(t) === 'borrow');
  const buyTasks    = allWithIdx.filter(t => bucketOf(t) === 'buy');

  // Selection helpers
  const isSelected = (cat, idx) => rs.selected[cat]?.includes(idx);
  const toggleTask = (cat, idx) => {
    const cur = rs.selected[cat] || [];
    const next = cur.includes(idx) ? cur.filter(i => i !== idx) : [...cur, idx];
    updateRole(activeRole, { selected: { ...rs.selected, [cat]: next } });
  };
  const toggleAll = (cat, taskList) => {
    const indices = taskList.map(t => t.idx);
    const allSelected = indices.every(i => rs.selected[cat]?.includes(i));
    updateRole(activeRole, { selected: { ...rs.selected, [cat]: allSelected ? [] : indices } });
  };

  const allSelected = [...new Set([...(rs.selected.bot || []), ...(rs.selected.build || []), ...(rs.selected.borrow || []), ...(rs.selected.buy || [])])];
  const freedTasks = allSelected.map(i => ({ ...tasks[i], idx: i, hrs: taskHrs(tasks[i]) }));
  const freedHrs = freedTasks.reduce((s, t) => s + t.hrs, 0);
  const selectedCount = allSelected.length;
  const canRedeploy = selectedCount >= 3;
  const retainedPct = tasks.length > 0 ? Math.round((1 - selectedCount / tasks.length) * 100) : 100;
  const costReductionPct = Math.round(freedHrs * AVG_HOURLY / Math.max(totalHrs * AVG_HOURLY, 1) * 100);

  // Phase 2 metrics
  const allocKeys = Object.keys(rs.allocations);
  const allocatedHrs = allocKeys.reduce((s, k) => s + (rs.allocations[k]?.hrs || 0), 0);
  const gapClosed = allocKeys.reduce((s, k) => {
    const a = rs.allocations[k];
    if (a?.target === 'automation') return s + a.hrs * AVG_HOURLY * 0.6;
    return s;
  }, 0);
  const capacityFTE = (freedHrs / 2080);
  const sameHrs = allocKeys.reduce((s, k) => rs.allocations[k]?.target === 'same_role' ? s + rs.allocations[k].hrs : s, 0);
  const prodIndex = totalHrs > 0 ? Math.round((totalHrs - freedHrs + sameHrs) / totalHrs * 100) : 50;
  const transScore = Math.min(100, (role.discipline_potential || 50) + Math.round(allocatedHrs / Math.max(freedHrs, 1) * 25));

  // Redeploy helpers
  const allocateTask = (taskKey, target, label, hrs) => {
    const next = { ...rs.allocations, [taskKey]: { target, label, hrs } };
    updateRole(activeRole, { allocations: next });
    setSelectedFreedTask(null);
    // Trigger nudge check
    checkNudges(next);
  };

  const addNewRole = () => {
    if (!newRoleName.trim() || !selectedFreedTask) return;
    const t = freedTasks.find(ft => `${ft.idx}` === `${selectedFreedTask}`);
    if (!t) return;
    const existing = rs.newRoles || [];
    const found = existing.findIndex(r => r.name === newRoleName.trim());
    let next;
    if (found >= 0) {
      next = [...existing];
      next[found] = { ...next[found], tasks: [...next[found].tasks, { task: t.task, hrs: t.hrs }] };
    } else {
      next = [...existing, { name: newRoleName.trim(), tasks: [{ task: t.task, hrs: t.hrs }] }];
    }
    updateRole(activeRole, { newRoles: next });
    allocateTask(`${t.idx}`, 'new_role', newRoleName.trim(), t.hrs);
  };

  // Nudge logic
  const checkNudges = (allocs) => {
    const botKeys = Object.keys(allocs).filter(k => allocs[k].target === 'automation');
    const unallocBorrow = borrowTasks.filter(t => !allocs[`${t.idx}`]);
    const buildWithHighBot = freedTasks.filter(t => allocs[`${t.idx}`]?.target === 'di' && (t.bot_score || t.automation_score || 0) > 0.7);

    if (botKeys.length > 0 && botKeys.length < botTasks.length && !nudge) {
      const next = botTasks.find(t => !allocs[`${t.idx}`]);
      if (next) {
        setNudge({
          text: `You automated a similar task. "${next.task.slice(0, 60)}..." is typically automated together. Add it?`,
          actions: [
            { label: 'Add', onClick: () => { allocateTask(`${next.idx}`, 'automation', 'AI/Automation', next.hrs); setNudge(null); } },
            { label: 'Skip', onClick: () => setNudge(null) },
          ],
        });
        return;
      }
    }
    if (unallocBorrow.length > 3 && !nudge) {
      setNudge({
        text: `${unallocBorrow.length} high-impact borrow tasks are unallocated. Redeploying them would increase your Productivity Index by ~12%.`,
        actions: [
          { label: 'Show tasks', onClick: () => setNudge(null) },
          { label: 'Dismiss', onClick: () => setNudge(null) },
        ],
      });
      return;
    }
    if (buildWithHighBot.length > 0 && !nudge) {
      const t = buildWithHighBot[0];
      setNudge({
        text: `Moving "${t.task.slice(0, 50)}..." to Automation instead of D&I would improve ASBC gap by an additional ${'\u00A3'}${Math.round(t.hrs * AVG_HOURLY * 0.6 / 1000)}K. Switch?`,
        actions: [
          { label: 'Switch', onClick: () => { allocateTask(`${t.idx}`, 'automation', 'AI/Automation', t.hrs); setNudge(null); } },
          { label: 'Keep D&I', onClick: () => setNudge(null) },
        ],
      });
    }
  };

  const goPhase = (p) => updateRole(activeRole, { phase: p });

  // Blueprint generation
  const readyRoles = Object.values(roleStates).filter(r => {
    const sel = r.selected || {};
    const totalSelected = (sel.bot?.length || 0) + (sel.build?.length || 0) + (sel.borrow?.length || 0) + (sel.buy?.length || 0);
    return totalSelected > 0 || r.phase >= 2 || Object.keys(r.allocations || {}).length > 0;
  });

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const sid = localStorage.getItem('reai_session_id') || localStorage.getItem('analysis_session_id');
      const vectors = {};
      const redeployments = {};
      roles.forEach((r, i) => {
        const st = roleStates[i] || {};
        vectors[r.role_title] = {
          automation: (st.selected?.bot?.length || 0) > 0,
          di: (st.selected?.build?.length || 0) > 0,
          discipline: (st.selected?.borrow?.length || 0) > 0,
          buy: (st.selected?.buy?.length || 0) > 0,
        };
        if (Object.keys(st.allocations || {}).length > 0 || (st.newRoles || []).length > 0) {
          redeployments[r.role_title] = { allocations: st.allocations || {}, new_roles: st.newRoles || [] };
        }
      });
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analysis/generate-blueprint`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ session_id: sid, selected_vectors: vectors, redeployments }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Blueprint generation failed');
      }
      const result = await res.json();
      localStorage.setItem('analysis_blueprint', JSON.stringify(result));
      if (result.blueprint_id) {
        localStorage.setItem('reai_blueprint_id', result.blueprint_id);
      }

      // Seed reai_role so the product-flow screens (Deconstruct / Analyze /
      // Sort · 4B / Recompose) find role data when opened from here.
      try {
        const firstRole = roles[0];
        if (firstRole) {
          const tasks = firstRole.task_breakdown || [];
          const reaiRole = {
            name: firstRole.original_title || firstRole.role_title || 'Role',
            function: func || '',
            hours: 40,
            activities: [
              {
                name: 'All tasks',
                hours: tasks.reduce((s, t) => s + (t.estimated_hours || 0), 0),
                subs: tasks.map((t) => {
                  const bot = t.bot_score || t.automation_score || 0;
                  const build = t.build_score || t.di_score || 0;
                  return {
                    name: t.task,
                    hours: t.estimated_hours || 0,
                    deployment: t.category_4b || 'build',
                    opportunity: Math.round(Math.max(bot, build * 0.8) * 100),
                  };
                }),
              },
            ],
          };
          localStorage.setItem('reai_role', JSON.stringify(reaiRole));
        }
      } catch { /* non-blocking — if role seeding fails, blueprint still loads */ }

      navigate('/blueprint/cockpit');
    } catch (err) {
      console.error(err);
      setInlineMsg({ type: 'error', title: 'Blueprint generation failed', message: 'Your analysis is saved. Try clicking Generate Blueprint again.', actions: [{ label: 'Try Again', onClick: () => { setInlineMsg(null); handleGenerate(); } }] });
    } finally {
      setGenerating(false);
    }
  };

  /* ── RENDER ─────────────────────────────────────────── */
  const existingTargets = REDEPLOY_TARGETS[func] || REDEPLOY_TARGETS.Operations;

  return (
    <div style={{ fontFamily: 'var(--fs)', minHeight: '100vh', background: C.bg, color: C.white, position: 'relative', zIndex: 2 }}>
      {/* ── Escape-hatch nav to top-level screens ───── */}
      <header className="nav">
        <span className="nav-brand">re<span className="ai">AI</span>magination</span>
        <span className="nav-sep" />
        <span className="nav-lbl">Analysis results</span>
        <nav className="nav-pages" style={{ marginLeft: 'auto' }}>
          <button onClick={() => navigate('/signal-room')}     className="np">Signal Room</button>
          <button onClick={() => navigate('/intelligence')}    className="np">Intelligence</button>
          <button onClick={() => navigate('/blueprint/cockpit')} className="np">Blueprint</button>
          <button onClick={() => navigate('/stage')}           className="np">Stage</button>
          <button onClick={() => navigate('/showcase/ai-tools')} className="np">Showcase</button>
        </nav>
      </header>
      {/* ── Unified dark shell (recipe 3.15) ─────────── */}
      <div style={{ paddingBottom: 100, paddingTop: 'var(--nav-h)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 40px 0' }}>
          {/* Stepper (recipe 3.15) */}
          <div className="stepper" style={{ marginBottom: 16 }}>
            <div className="stepper-top">
              <span className="stepper-label">Step 3 of 4</span>
              <button className="stepper-back" onClick={() => navigate('/analysis/discipline')}>← Back</button>
            </div>
            <div className="stepper-rail">
              <div className="stepper-fill" style={{ width: '75%' }} />
            </div>
          </div>

          <HintBar screenId="analysis-results" hints={HINTS} />

          <h1 className="h1" style={{ margin: '8px 0 6px', fontSize: 32 }}>
            Transformation potential identified <InfoTip position="right" width={280} title="What does this mean?" text="We have analysed each role at task level and classified every task across the 4B framework - Bot, Borrow, Build, Buy. Your job is to review these classifications, toggle any you disagree with, and confirm which tasks to act on." />
          </h1>
          <p style={{ fontSize: 14, color: 'var(--t3)', marginBottom: restoredFromSession ? 8 : 20 }}>
            {roles.length} roles · {func} · {DISC_LABELS[discipline] || discipline}
          </p>
          {restoredFromSession && (
            <p className="eyebrow eye-teal" style={{ marginBottom: 20 }}>
              Analysis saved · <button onClick={() => { localStorage.removeItem('analysis_mol_results'); setData(null); setRestoredFromSession(false); navigate('/analysis/discipline'); }} style={{ background: 'none', border: 'none', color: 'var(--teal)', textDecoration: 'underline', cursor: 'pointer', fontSize: 10, padding: 0, fontFamily: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>Refresh to re-run</button>
            </p>
          )}

          {/* Role tab strip (recipe 3.15 .role-tabs) */}
          <div className="role-tabs">
            {roles.map((r, i) => (
              <button
                key={i}
                className={`ct${activeRole === i ? ' on' : ''}`}
                onClick={() => { setActiveRole(i); setSelectedFreedTask(null); setNudge(null); }}
              >
                {r.original_title || r.role_title || 'Role ' + (i + 1)}
              </button>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 40px' }}>

          {/* Phase tabs */}
          <div style={{ display: 'flex', gap: 4, padding: '20px 0 24px' }}>
            {[
              { n: 1, label: '1 - Select tasks' },
              { n: 2, label: '2 - Redeploy capacity' },
              { n: 3, label: '3 - Redesigned role' },
            ].map((ph) => {
              const unlocked = ph.n <= rs.phase || (ph.n === 2 && canRedeploy) || (ph.n === 3 && Object.keys(rs.allocations).length > 0);
              const active = rs.phase === ph.n;
              return (
                <button key={ph.n} onClick={() => unlocked && goPhase(ph.n)} style={{
                  padding: '8px 20px', fontSize: 12, fontWeight: 600,
                  fontFamily: "'Overpass Mono', monospace", letterSpacing: '0.06em',
                  background: active ? `${C.teal}18` : 'transparent',
                  border: `1px solid ${active ? C.teal : C.border}`,
                  color: active ? C.teal : unlocked ? C.muted : C.fog,
                  borderRadius: 8, cursor: unlocked ? 'pointer' : 'default',
                  opacity: unlocked ? 1 : 0.4, transition: 'all 0.2s',
                  textTransform: 'uppercase',
                }}>
                  {ph.label}
                </button>
              );
            })}
          </div>

          {/* ════════════════════════════════════════════
             PHASE 1 - SELECT TASKS
             ════════════════════════════════════════════ */}
          {rs.phase === 1 && (
            <div style={{ animation: 'slideIn 300ms ease-out' }}>
              {/* Filled/Vacant badge */}
              <div style={{ marginBottom: 16 }}>
                <span style={{
                  fontFamily: "'Overpass Mono', monospace", fontSize: 11, padding: '4px 12px',
                  borderRadius: 99, background: isFilled ? 'rgba(255,255,255,0.06)' : `${C.teal}18`,
                  color: isFilled ? C.muted : C.teal,
                }}>
                  {isFilled ? '\u25CF Filled' : '\u25CB Vacant \u2014 redesign at hire'}
                </span>
              </div>

              {/* 4B columns (recipe 3.15 .task-cols / .task-col-hd.hd-*) */}
              <div className={`task-cols ${buyTasks.length > 0 ? 'task-cols-4' : 'task-cols-3'}`} style={{ marginBottom: 24 }}>
                {/* Bot column */}
                <div>
                  <div className="task-col-hd hd-bot">
                    <span>BOT</span>
                    <InfoTip position="right" width={260} title="BOT - Automate" text="This task can be fully or partially handled by AI. Selecting BOT tasks frees your people for higher-value work and reduces cost. The sub-task view shows a realistic vs theoretical automation split." />
                    <span className="col-count">{botTasks.length} tasks</span>
                    <button onClick={() => toggleAll('bot', botTasks)} style={{ fontSize: 10, color: 'var(--t4)', background: 'none', border: '1px solid var(--line-s)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', marginLeft: 8 }}>Toggle all</button>
                  </div>
                  {botTasks.map((t) => (
                    <TaskCard key={t.idx} task={t.task} hours={t.estimated_hours || t.hrs} selected={isSelected('bot', t.idx)} color={C.violet} onToggle={() => toggleTask('bot', t.idx)} subLabel={t.sub_label} roleTitle={role.role_title} roleFunction={func} category4b="bot" />
                  ))}
                  {botTasks.length === 0 && <p style={{ fontSize: 12, color: 'var(--t4)' }}>No automatable tasks identified</p>}
                </div>

                {/* Build column */}
                <div>
                  <div className="task-col-hd hd-build">
                    <span>BUILD</span>
                    <InfoTip position="right" width={260} title="BUILD - Keep human" text="This task requires strategic thinking, judgment, or empathy that AI cannot replicate. BUILD tasks should stay with your people - they are your competitive advantage." />
                    <span className="col-count">{buildTasks.length} tasks</span>
                    <button onClick={() => toggleAll('build', buildTasks)} style={{ fontSize: 10, color: 'var(--t4)', background: 'none', border: '1px solid var(--line-s)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', marginLeft: 8 }}>Toggle all</button>
                  </div>
                  {buildTasks.map((t) => (
                    <TaskCard key={t.idx} task={t.task} hours={t.estimated_hours || t.hrs} selected={isSelected('build', t.idx)} color={C.teal} onToggle={() => toggleTask('build', t.idx)} subLabel={t.sub_label} roleTitle={role.role_title} roleFunction={func} category4b="build" />
                  ))}
                  {buildTasks.length === 0 && <p style={{ fontSize: 12, color: 'var(--t4)' }}>No build pathway tasks identified</p>}
                </div>

                {/* Borrow column */}
                <div>
                  <div className="task-col-hd hd-borrow">
                    <span>BORROW</span>
                    <InfoTip position="right" width={260} title="BORROW - Fractional or Interim" text="This task needs specialist expertise but not a full-time hire. BORROW means sourcing fractional, interim, or advisory talent through The Stage - faster and cheaper than permanent recruitment." />
                    <span className="col-count">{borrowTasks.length} tasks</span>
                    <button onClick={() => toggleAll('borrow', borrowTasks)} style={{ fontSize: 10, color: 'var(--t4)', background: 'none', border: '1px solid var(--line-s)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', marginLeft: 8 }}>Toggle all</button>
                  </div>
                  {borrowTasks.map((t) => (
                    <TaskCard key={t.idx} task={t.task} hours={t.estimated_hours || t.hrs} selected={isSelected('borrow', t.idx)} color={C.gold} onToggle={() => toggleTask('borrow', t.idx)} subLabel={t.sub_label} roleTitle={role.role_title} roleFunction={func} category4b="borrow" />
                  ))}
                  {borrowTasks.length === 0 && <p style={{ fontSize: 12, color: 'var(--t4)' }}>No borrow tasks identified</p>}
                </div>

                {/* Buy column (conditional) */}
                {buyTasks.length > 0 && (
                  <div>
                    <div className="task-col-hd hd-buy">
                      <span>BUY</span>
                      <InfoTip position="right" width={260} title="BUY - Permanent hire" text="This task requires a dedicated, permanent specialist. BUY signals that the work is too strategic or too continuous for fractional coverage." />
                      <span className="col-count">{buyTasks.length} tasks</span>
                      <button onClick={() => toggleAll('buy', buyTasks)} style={{ fontSize: 10, color: 'var(--t4)', background: 'none', border: '1px solid var(--line-s)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', marginLeft: 8 }}>Toggle all</button>
                    </div>
                    {buyTasks.map((t) => (
                      <TaskCard key={t.idx} task={t.task} hours={t.estimated_hours || t.hrs} selected={isSelected('buy', t.idx)} color={C.white} onToggle={() => toggleTask('buy', t.idx)} subLabel={t.sub_label} />
                    ))}
                  </div>
                )}
              </div>

              {/* Inline messages */}
              {inlineMsg && <InlineMessage {...inlineMsg} onDismiss={() => setInlineMsg(null)} />}

              {/* Summary bar */}
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div style={{ display: 'flex', gap: 24 }}>
                  {[
                    [`${freedHrs}`, 'hrs freed'],
                    [`${costReductionPct}%`, 'cost reduction'],
                    [`${selectedCount}`, 'tasks selected'],
                    [`${retainedPct}%`, 'FTE retained'],
                  ].map(([val, label]) => (
                    <div key={label}>
                      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: C.teal }}>{val}</span>
                      <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, display: 'block' }}>{label}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => { goPhase(2); }} disabled={!canRedeploy} style={{
                  padding: '12px 24px', fontSize: 14, fontWeight: 600,
                  background: canRedeploy ? C.gold : C.fog, color: canRedeploy ? C.bg : C.muted,
                  border: 'none', borderRadius: 8, cursor: canRedeploy ? 'pointer' : 'not-allowed',
                  fontFamily: "'Inter', sans-serif", transition: 'all 0.2s',
                }}>
                  {canRedeploy ? <>Redeploy Capacity → <InfoTip position="top" width={280} title="What is Redeploy Capacity?" text="You have selected tasks to automate or outsource. This step decides what happens to the hours freed up - do they go to another task, another person, or get released entirely? Without this step, freed capacity just disappears on paper." /></> : `Select ${3 - selectedCount} more tasks`}
                </button>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════
             PHASE 2 - REDEPLOY CAPACITY
             ════════════════════════════════════════════ */}
          {rs.phase === 2 && (
            <div style={{ animation: 'slideIn 300ms ease-out' }}>
              {/* Nudge */}
              {nudge && <NudgeCard text={nudge.text} actions={nudge.actions} onDismiss={() => setNudge(null)} />}

              {/* Phase 2 explanation banner */}
              <div style={{ background: 'rgba(0,212,200,0.06)', border: '1px solid rgba(0,212,200,0.2)', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>↻</span>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600, color: '#F2F6FA', marginBottom: 4 }}>
                    Step 2 of 2 - Where does the freed capacity go?
                    <InfoTip position="bottom" width={300} title="Why is there a Step 2?" text="In Step 1 you identified which tasks should be automated (BOT), outsourced (BORROW), or permanently hired (BUY). That frees up hours currently spent on those tasks. Step 2 decides what happens to those freed hours - redirect them to strategic work, reassign them, or release them. Without this decision, your Blueprint cannot calculate the true financial impact." />
                  </p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#AAB8C8', lineHeight: 1.6 }}>
                    Your selected tasks are now in the <strong style={{ color: '#00D4C8' }}>Freed Capacity Pool</strong> on the left. Click each one and choose where those hours go. The metrics on the right update in real time as you allocate.
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20 }}>
                {/* LEFT - Freed pool */}
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, maxHeight: 600, overflowY: 'auto' }}>
                  <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.teal, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Freed capacity pool</p>
                  {/* Capacity bar */}
                  <div style={{ height: 6, background: C.card, borderRadius: 3, overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ width: freedHrs > 0 ? `${Math.round(allocatedHrs / freedHrs * 100)}%` : '0%', height: '100%', background: C.teal, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                  <p style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>
                    {allocatedHrs} of {freedHrs} hrs allocated
                  </p>
                  {freedTasks.map((t) => {
                    const key = `${t.idx}`;
                    const allocated = !!rs.allocations[key];
                    const isSel = selectedFreedTask === key;
                    return (
                      <button key={key} onClick={() => !allocated && setSelectedFreedTask(isSel ? null : key)} style={{
                        width: '100%', textAlign: 'left', padding: '10px 12px',
                        background: allocated ? `${C.teal}08` : isSel ? `${C.teal}15` : C.card,
                        border: `1px solid ${isSel ? C.teal : allocated ? `${C.teal}30` : C.border}`,
                        borderRadius: 8, cursor: allocated ? 'default' : 'pointer',
                        marginBottom: 6, opacity: allocated ? 0.5 : 1,
                        transition: 'all 0.2s', display: 'block',
                      }}>
                        <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 13, color: C.white, lineHeight: 1.4 }}>
                          {t.task.length > 50 ? t.task.slice(0, 50) + '...' : t.task}
                        </p>
                        <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: allocated ? C.teal : C.muted }}>
                          {allocated ? `\u2713 ${rs.allocations[key].label}` : `${t.hrs} hrs`}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* RIGHT */}
                <div>
                  {/* Top metrics */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                    <MetricCard label="BOT SAVINGS" sub="AI-addressable cost freed">
                      <AnimNum value={gapClosed} prefix={'\u00A3'} />
                    </MetricCard>
                    <MetricCard label="BUILD CAPACITY" sub="FTE equivalent created">
                      <AnimNum value={capacityFTE} decimals={1} suffix=" FTE" />
                    </MetricCard>
                    <MetricCard label="BORROW PIPELINE" sub="fractional/interim hrs">
                      <AnimNum value={prodIndex} suffix="%" />
                    </MetricCard>
                    <MetricCard label="BUY REQUIREMENT" sub="new hire positions">
                      <AnimNum value={transScore} />
                    </MetricCard>
                  </div>

                  {/* Redeploy targets */}
                  {selectedFreedTask ? (
                    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                      <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 16 }}>
                        Redeploy: "{(freedTasks.find(t => `${t.idx}` === selectedFreedTask)?.task || '').slice(0, 40)}..."
                      </p>

                      {/* Option: BOT - Automate */}
                      <div style={{ marginBottom: 14 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: '#7C5CFC', marginBottom: 4 }}>BOT / AI-Addressable</p>
                        <p style={{ fontSize: 11, color: C.fog, marginBottom: 8 }}>
                          {isFilled ? 'Automate this task to free capacity for the incumbent' : 'Remove from JD and automate with AI tooling'}
                        </p>
                        <button onClick={() => {
                          const ft = freedTasks.find(t => `${t.idx}` === selectedFreedTask);
                          if (ft) allocateTask(selectedFreedTask, 'automation', 'BOT / AI-Addressable', ft.hrs);
                        }} style={{
                          width: '100%', textAlign: 'left', padding: '8px 12px', marginBottom: 4,
                          background: C.card, border: `1px solid #7C5CFC30`, borderRadius: 8,
                          cursor: 'pointer', fontSize: 13, color: '#7C5CFC',
                        }}>
                          Allocate to AI/Automation
                          <span style={{ display: 'block', fontSize: 10, color: C.fog, marginTop: 2 }}>Find matching tools in Showcase</span>
                        </button>
                      </div>

                      {/* Option: BUILD - Human Strategic */}
                      <div style={{ marginBottom: 14 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: C.teal, marginBottom: 4 }}>BUILD / Human Strategic</p>
                        <p style={{ fontSize: 11, color: C.fog, marginBottom: 8 }}>
                          {isFilled ? 'Senior hours freed from admin for strategic work' : 'Core strategic tasks for the redesigned JD'}
                        </p>
                        {buildTasks.filter(t => !rs.allocations[`${t.idx}`]).slice(0, 2).map((dt) => (
                          <button key={dt.idx} onClick={() => {
                            const ft = freedTasks.find(t => `${t.idx}` === selectedFreedTask);
                            if (ft) allocateTask(selectedFreedTask, 'same_role', 'BUILD / Human Strategic', ft.hrs);
                          }} style={{
                            width: '100%', textAlign: 'left', padding: '8px 12px', marginBottom: 4,
                            background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
                            cursor: 'pointer', fontSize: 13, color: C.white,
                          }}>
                            {dt.task.length > 55 ? dt.task.slice(0, 55) + '...' : dt.task}
                            <span style={{ display: 'block', fontSize: 10, color: C.teal, marginTop: 2 }}>{dt.estimated_hours || dt.hrs} hrs</span>
                          </button>
                        ))}
                      </div>

                      {/* Option B: BUILD - Inclusion Pathway */}
                      <div style={{ marginBottom: 14 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: C.green, marginBottom: 4 }}>BUILD / Inclusion Pathway</p>
                        <p style={{ fontSize: 11, color: C.fog, marginBottom: 8 }}>
                          {isFilled ? 'Redesign lower-complexity tasks for inclusive hiring' : 'Design role inclusively from start'}
                        </p>
                        <button onClick={() => {
                          const ft = freedTasks.find(t => `${t.idx}` === selectedFreedTask);
                          if (ft) allocateTask(selectedFreedTask, 'di', 'BUILD / Inclusion Pathway', ft.hrs);
                        }} style={{
                          width: '100%', textAlign: 'left', padding: '8px 12px', marginBottom: 4,
                          background: C.card, border: `1px solid ${C.green}30`, borderRadius: 8,
                          cursor: 'pointer', fontSize: 13, color: C.green,
                        }}>
                          Allocate to Inclusion Pathway
                        </button>
                      </div>

                      {/* Option C: BORROW - activate search */}
                      <div style={{ marginBottom: 14 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: C.gold, marginBottom: 4 }}>BORROW</p>
                        <p style={{ fontSize: 11, color: C.fog, marginBottom: 8 }}>Activate fractional, interim, or advisory search in Stage</p>
                        {['Fractional', 'Interim', 'Advisory'].map((type) => (
                          <button key={type} onClick={() => {
                            const ft = freedTasks.find(t => `${t.idx}` === selectedFreedTask);
                            if (ft) allocateTask(selectedFreedTask, 'existing_role', `BORROW / ${type}`, ft.hrs);
                          }} style={{
                            width: '100%', textAlign: 'left', padding: '8px 12px', marginBottom: 4,
                            background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
                            cursor: 'pointer', fontSize: 13, color: C.white,
                          }}>
                            Activate {type} search in Stage
                          </button>
                        ))}
                      </div>

                      {/* Option D: BUY - New role */}
                      <div>
                        <p style={{ fontSize: 12, fontWeight: 600, color: C.white, marginBottom: 4 }}>BUY / New Role</p>
                        <p style={{ fontSize: 11, color: C.fog, marginBottom: 8 }}>Aggregate freed capacity into a new position</p>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="Name this role..." style={{
                            flex: 1, padding: '8px 12px', fontSize: 13, background: C.card,
                            border: `1px solid ${C.border}`, borderRadius: 8, color: C.white, outline: 'none',
                          }} />
                          <button onClick={addNewRole} disabled={!newRoleName.trim()} style={{
                            padding: '8px 16px', fontSize: 12, fontWeight: 600,
                            background: newRoleName.trim() ? C.violet : C.fog,
                            color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer',
                          }}>Add</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 40, textAlign: 'center' }}>
                      <p style={{ fontSize: 14, color: C.muted }}>Select a task from the freed pool to redeploy it</p>
                    </div>
                  )}

                  {/* Before/After toggle */}
                  <button onClick={() => setShowBeforeAfter(!showBeforeAfter)} style={{
                    marginTop: 16, width: '100%', padding: '10px 16px', fontSize: 12,
                    fontFamily: "'Overpass Mono', monospace", color: C.muted,
                    background: 'transparent', border: `1px solid ${C.border}`,
                    borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                  }}>
                    {showBeforeAfter ? '\u25B2' : '\u25BC'} Show Before / After
                  </button>

                  {showBeforeAfter && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
                      {/* Before */}
                      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, marginBottom: 12, textTransform: 'uppercase' }}>Current role architecture</p>
                        {tasks.map((t, i) => (
                          <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.white }}>
                            {t.task.length > 45 ? t.task.slice(0, 45) + '...' : t.task}
                            <span style={{ float: 'right', color: C.fog, fontFamily: "'Overpass Mono', monospace", fontSize: 10 }}>FTE</span>
                          </div>
                        ))}
                      </div>
                      {/* After */}
                      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
                        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.teal, marginBottom: 12, textTransform: 'uppercase' }}>Redesigned role architecture</p>
                        {tasks.map((t, i) => {
                          const key = `${i}`;
                          const alloc = rs.allocations[key];
                          const isFreed = allSelected.includes(i);
                          return (
                            <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: isFreed ? C.fog : C.white, textDecoration: alloc?.target === 'automation' ? 'line-through' : 'none' }}>
                              {t.task.length > 40 ? t.task.slice(0, 40) + '...' : t.task}
                              <span style={{ float: 'right', fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: alloc ? C.teal : isFreed ? C.amber : C.fog }}>
                                {alloc ? `\u2192 ${alloc.label.slice(0, 12)}` : isFreed ? 'freed' : 'FTE'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Phase 2 CTA */}
                  <div style={{ marginTop: 20, textAlign: 'right' }}>
                    <button onClick={() => goPhase(3)} disabled={allocatedHrs === 0} style={{
                      padding: '12px 24px', fontSize: 14, fontWeight: 600,
                      background: allocatedHrs > 0 ? C.gold : C.fog, color: allocatedHrs > 0 ? C.bg : C.muted,
                      border: 'none', borderRadius: 8, cursor: allocatedHrs > 0 ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s',
                    }}>
                      View Redesigned Role &rarr;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════
             PHASE 3 - REDESIGNED ROLE
             ════════════════════════════════════════════ */}
          {rs.phase === 3 && (
            <div style={{ animation: 'slideIn 300ms ease-out' }}>
              {/* Urgency badge */}
              <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 10, background: isFilled ? `${C.amber}10` : `${C.teal}10`, border: `1px solid ${isFilled ? `${C.amber}30` : `${C.teal}30`}` }}>
                <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: isFilled ? C.amber : C.teal }}>
                  {isFilled
                    ? `Phased transformation \u00B7 ${Math.round((role.transformation_timeline_multiplier || 1) * 16)} weeks \u00B7 Change management required`
                    : 'Immediate \u00B7 Redesign JD now'}
                </p>
                <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{role.change_management_note || ''}</p>
              </div>

              {/* Full before/after */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
                {/* Before */}
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
                  <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: C.muted, marginBottom: 16 }}>Current: {role.role_title}</p>
                  {tasks.map((t, i) => (
                    <div key={i} style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontFamily: "'Instrument Serif', serif", fontSize: 13, color: C.white }}>{t.task}</span>
                      <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.fog, flexShrink: 0, marginLeft: 12 }}>FTE</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 12, padding: '8px 12px', background: C.card, borderRadius: 8 }}>
                    <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted }}>{tasks.length} tasks - current state (all FTE)</span>
                  </div>
                </div>

                {/* After: Redesigned task list + node diagram */}
                <div style={{ background: C.surface, border: `1px solid ${C.teal}20`, borderRadius: 12, padding: 24 }}>
                  <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: C.teal, marginBottom: 16 }}>Redesigned: {role.role_title}</p>
                  {/* Task list with 4B assignments */}
                  {tasks.map((t, i) => {
                    // Determine 4B assignment: user selection > task category_4b > FTE
                    let cat = 'fte';
                    let catLabel = 'FTE';
                    let catColor = C.fog;
                    if (rs.selected.bot?.includes(i)) { cat = 'bot'; catLabel = 'BOT'; catColor = '#7C5CFC'; }
                    else if (rs.selected.borrow?.includes(i)) { cat = 'borrow'; catLabel = 'BORROW'; catColor = '#C9A84C'; }
                    else if (rs.selected.build?.includes(i)) { cat = 'build'; catLabel = 'BUILD'; catColor = '#00D4C8'; }
                    else if (rs.selected.buy?.includes(i)) { cat = 'buy'; catLabel = 'BUY'; catColor = '#F2F6FA'; }
                    else if (t.category_4b === 'bot') { cat = 'bot'; catLabel = 'BOT'; catColor = '#7C5CFC'; }
                    else if (t.category_4b === 'borrow') { cat = 'borrow'; catLabel = 'BORROW'; catColor = '#C9A84C'; }
                    else if (t.category_4b === 'buy') { cat = 'buy'; catLabel = 'BUY'; catColor = '#F2F6FA'; }
                    else if (t.sub_label === 'inclusion_pathway') { cat = 'build'; catLabel = 'BUILD'; catColor = '#2EC8A0'; }
                    const isTransformed = cat !== 'fte';
                    return (
                      <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontFamily: "'Instrument Serif', serif", fontSize: 12, color: isTransformed && cat === 'bot' ? C.fog : C.white, textDecoration: cat === 'bot' ? 'line-through' : 'none', flex: 1 }}>
                          {t.task.length > 45 ? t.task.slice(0, 45) + '...' : t.task}
                        </span>
                        <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, padding: '2px 6px', borderRadius: 4, flexShrink: 0, marginLeft: 8, background: `${catColor}18`, color: catColor }}>
                          {catLabel}
                        </span>
                      </div>
                    );
                  })}
                  {/* After-column summary */}
                  {(() => {
                    const s = rs.selected || {};
                    const botN = (s.bot || []).length;
                    const borrowN = (s.borrow || []).length;
                    const buildN = (s.build || []).length;
                    const buyN = (s.buy || []).length;
                    const fteN = tasks.length - botN - borrowN - buildN - buyN;
                    const transformed = botN + borrowN + buildN + buyN;
                    return (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: C.card, borderRadius: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontFamily: "'Overpass Mono', monospace", fontSize: 10 }}>
                        {fteN > 0 && <span style={{ color: C.fog }}>{fteN} FTE</span>}
                        {botN > 0 && <span style={{ color: '#7C5CFC' }}>{botN} BOT</span>}
                        {buildN > 0 && <span style={{ color: '#00D4C8' }}>{buildN} BUILD</span>}
                        {borrowN > 0 && <span style={{ color: '#C9A84C' }}>{borrowN} BORROW</span>}
                        {buyN > 0 && <span style={{ color: '#F2F6FA' }}>{buyN} BUY</span>}
                        {transformed > 0 && <span style={{ color: C.muted }}> · {transformed} transformed</span>}
                      </div>
                    );
                  })()}
                  <div style={{ height: 1, background: C.border, margin: '16px 0' }} />

                  {/* Node diagram */}
                  {(() => {
                    // Read directly from the same selected state as footer
                    const sel = rs.selected || {};
                    const botN = (sel.bot || []).length;
                    const buildN = (sel.build || []).length;
                    const borrowN = (sel.borrow || []).length;
                    const buyN = (sel.buy || []).length;

                    const nodeData = [
                      { label: 'AI Tasks', count: botN, color: '#7C5CFC' },
                      { label: 'Strategic', count: buildN, color: '#00D4C8' },
                      { label: 'Borrow', count: borrowN, color: '#C9A84C' },
                      { label: 'Buy', count: buyN, color: '#F2F6FA' },
                    ].filter(n => n.count > 0);

                    // Fixed angle positions based on satellite count
                    const anglePresets = {
                      1: [270],
                      2: [270, 90],
                      3: [270, 150, 30],
                      4: [270, 0, 90, 180],
                    };
                    const angles = anglePresets[nodeData.length] || anglePresets[4] || [];
                    const toRad = (deg) => (deg * Math.PI) / 180;
                    const cx = 160, cy = 130, cr = 50, sr = 35, orbit = 95;
                    const roleLabel = role.original_title || role.role_title || '';
                    const truncLabel = roleLabel.length > 12 ? roleLabel.slice(0, 12) + '...' : roleLabel;

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <svg width={320} height={270} viewBox="0 0 320 270">
                          {/* Centre node */}
                          <circle cx={cx} cy={cy} r={cr} fill="rgba(0,212,200,0.08)" stroke="#00D4C8" strokeWidth="2" />
                          <text x={cx} y={cy + 4} textAnchor="middle" fill={C.white} fontSize="10" fontFamily="'Overpass Mono', monospace">
                            {truncLabel}
                          </text>
                          {/* Satellite nodes */}
                          {nodeData.map((node, i) => {
                            const angle = toRad(angles[i] || 0);
                            const sx = cx + Math.cos(angle) * orbit;
                            const sy = cy + Math.sin(angle) * orbit;
                            return (
                              <g key={node.label}>
                                <line x1={cx} y1={cy} x2={sx} y2={sy} stroke={node.color} strokeWidth="1" strokeDasharray="4 3" opacity="0.4" />
                                <circle cx={sx} cy={sy} r={sr} fill={`${node.color}15`} stroke={node.color} strokeWidth="1.5" />
                                <text x={sx} y={sy - 4} textAnchor="middle" fill={node.color} fontSize="9" fontFamily="'Overpass Mono', monospace">{node.label}</text>
                                <text x={sx} y={sy + 12} textAnchor="middle" fill={node.color} fontSize="14" fontWeight="700" fontFamily="'Bebas Neue', sans-serif">{node.count}</text>
                              </g>
                            );
                          })}
                        </svg>

                        {/* Legend */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', justifyContent: 'center', marginTop: 8 }}>
                          {[
                            ['#7C5CFC', 'BOT / AI-Addressable'],
                            ['#C9A84C', 'BORROW / Fractional'],
                            ['#FF3B5C', 'BORROW / Interim'],
                            ['#4A9EFF', 'BORROW / Advisory'],
                            ['#00D4C8', 'BUILD / Human Strategic'],
                            ['#2EC8A0', 'BUILD / Inclusion Pathway'],
                            ['#F2F6FA', 'BUY / New Hire'],
                          ].map(([col, label]) => (
                            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.muted }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: col, display: 'inline-block' }} />
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* New roles created */}
              {(rs.newRoles || []).length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.violet, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>New roles created</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                    {rs.newRoles.map((nr, i) => (
                      <div key={i} style={{ background: C.card, border: `1px solid ${C.violet}30`, borderRadius: 10, padding: 16 }}>
                        <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: C.violet, marginBottom: 8 }}>{nr.name}</p>
                        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.muted, marginBottom: 8 }}>
                          {nr.tasks.length} tasks - {nr.tasks.reduce((s, t) => s + t.hrs, 0)} hrs/yr
                        </p>
                        {nr.tasks.map((t, j) => (
                          <p key={j} style={{ fontSize: 12, color: C.white, padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
                            {t.task.length > 50 ? t.task.slice(0, 50) + '...' : t.task}
                          </p>
                        ))}
                        <span style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.violet, marginTop: 8, display: 'inline-block' }}>Added to Blueprint</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Fixed bottom bar (recipe 3.15 sticky footer) ── */}
        <div className="glass-strong" style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          padding: '14px 40px', zIndex: 100,
        }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <span className="eyebrow">
              <strong style={{ color: 'var(--teal)' }}>{readyRoles.length}</strong> of {roles.length} roles configured
              {readyRoles.length > 0 && readyRoles.length < roles.length && (
                <span style={{ color: 'var(--sig-amber)', marginLeft: 8 }}>· Configure remaining roles for a complete Blueprint</span>
              )}
            </span>
            <button
              className="btn-teal"
              onClick={handleGenerate}
              disabled={generating}
              style={{
                opacity: generating ? 0.6 : 1,
                cursor: generating ? 'wait' : 'pointer',
              }}
            >
              {generating ? 'Building Blueprint…' : 'Generate Blueprint →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AnalysisResultsWithExplainer = (props) => (<><AnalysisResults {...props} /><DemoExplainer screen="ANALYSIS_RESULTS" /></>);
export default AnalysisResultsWithExplainer;
